import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Crown, Landmark } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { RegHeatMap } from "./regmap/RegHeatMap";
import { AlertsRail, PipelinePanel, TrendsPanel, MoversPanel, CalendarPanel, PremiumRail } from "./regmap/RegPanels";
import { PANEL, Flag, LevelChip, InfoTip, AreaSpark, deltaTone } from "./regmap/regShared";

const SPOT_STYLE = {
  background: "linear-gradient(180deg, #0a0910 0%, #040406 100%)",
  boxShadow: "inset 0 0 0 1px rgba(139,92,255,0.42), 0 16px 48px rgba(18,8,48,0.3)",
};
const METRIC_STYLE = {
  background: "linear-gradient(180deg, #0b0b11 0%, #050507 100%)",
  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.06)",
};

const SentimentBar = () => (
  <div className="mt-2" aria-hidden>
    <div className="h-[6px] rounded-full opacity-35"
      style={{ background: "linear-gradient(90deg,#f33db4 0%,#8b5cff 35%,#14cfe6 70%,#22d38f 100%)" }} />
  </div>
);

const SENT_COLOR = (score: number) =>
  score >= 40 ? "#22d38f" : score >= 10 ? "#14cfe6" : score > -10 ? "#c3c9d8" : score > -40 ? "#8b5cff" : "#f33db4";

const SentimentSlider = ({ score }: { score: number }) => {
  const pct = Math.min(Math.max((score + 100) / 2, 3), 97);
  return (
    <div className="relative mt-2.5" role="img" aria-label={`Regulatory coverage tone score ${score} on a −100 to +100 scale`} data-testid="reg-sentiment-slider">
      <div className="h-[6px] rounded-full opacity-90"
        style={{ background: "linear-gradient(90deg,#f33db4 0%,#8b5cff 35%,#14cfe6 70%,#22d38f 100%)" }} />
      <span className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-[12px] h-[12px] rounded-full bg-white ring-2 ring-black/70 shadow-[0_0_10px_rgba(255,255,255,0.55)] motion-safe:transition-[left] motion-safe:duration-300"
        style={{ left: `${pct}%` }} aria-hidden />
    </div>
  );
};

const Spotlight = ({ j }: { j: any }) => {
  const enfDelta = j.enforcement_delta_pct;
  return (
    <div data-testid="reg-spotlight" className={`${PANEL} border-0 p-5 flex flex-col min-w-0`} style={SPOT_STYLE}>
      <h3 className="nl-geist font-bold text-[13px] text-[#eef1f9] tracking-[0.04em]">JURISDICTION SPOTLIGHT</h3>
      <div key={j.code} className="flex-1 flex flex-col motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:duration-300">
        <div className="flex items-center justify-between gap-3 mt-3.5">
          <span className="flex items-center gap-3 min-w-0">
            <Flag code={j.code} className="w-[34px] h-[23px]" />
            <p className="nl-sg font-medium text-[23px] text-[#f2f5fc] truncate" data-testid="reg-spotlight-name">{j.name}</p>
          </span>
          <LevelChip level={j.level} />
        </div>
        <p className="nl-geist text-[11px] text-[#9aa3b8] mt-2.5 leading-relaxed">
          {j.stories_30d} policy stories tracked over the last 30 days · {j.initiatives || "no"} tracked initiative{j.initiatives === 1 ? "" : "s"} · {j.enforcement_30d} enforcement-related.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mt-4">
          <div className="rounded-xl p-3.5 min-h-[96px]" style={METRIC_STYLE}>
            <div className="flex items-center gap-1.5">
              <p className="nl-geist font-bold text-[8.5px] tracking-[0.08em] text-[#ab8cff]">POLICY MOMENTUM</p>
              <InfoTip label="Policy momentum methodology">
                Coverage velocity: last 7 days vs the prior 7 days of NewsLayer policy stories for this jurisdiction.
              </InfoTip>
            </div>
            <div className="flex items-end justify-between gap-2 mt-2">
              <span className="nl-sg font-medium text-[22px] leading-none text-[#f2f5fc]">{j.momentum}</span>
              <AreaSpark points={j.daily_14d} color="#8b5cff" className="w-[92px] h-[26px]" />
            </div>
            <p className="nl-geist text-[9px] text-[#ab8cff] mt-1.5">{j.stories_30d} policy stories · 30D</p>
          </div>
          <div className="rounded-xl p-3.5 min-h-[96px]" style={METRIC_STYLE}>
            <div className="flex items-center gap-1.5">
              <p className="nl-geist font-bold text-[8.5px] tracking-[0.08em] text-[#14cfe6]">ENFORCEMENT (30D)</p>
              <InfoTip label="Enforcement methodology">
                Policy stories referencing enforcement action (suits, fines, sanctions, rulings). Derived from coverage.
              </InfoTip>
            </div>
            <div className="flex items-end justify-between gap-2 mt-2">
              <span className="nl-sg font-medium text-[22px] leading-none text-[#f2f5fc]">{j.enforcement_30d}</span>
              <AreaSpark points={j.enf_daily_14d} color="#14cfe6" className="w-[92px] h-[26px]" />
            </div>
            <p className="nl-geist text-[9px] mt-1.5" style={{ color: enfDelta == null ? "#6f7a94" : deltaTone(enfDelta) }}>
              {enfDelta == null ? "No prior-period data" : `${enfDelta > 0 ? "+" : ""}${enfDelta}% vs Prior 30D`}
            </p>
          </div>
          <div className="rounded-xl p-3.5 min-h-[96px]" style={METRIC_STYLE}>
            <div className="flex items-center gap-1.5">
              <p className="nl-geist font-bold text-[8.5px] tracking-[0.08em] text-[#c09aff]">LEGISLATIVE STATUS</p>
              <InfoTip label="Legislative status methodology">
                Active records for this jurisdiction in the NewsLayer Regulation Tracker that have not yet passed.
              </InfoTip>
            </div>
            <span className="block nl-sg font-medium text-[22px] leading-none text-[#f2f5fc] mt-2">{j.initiatives || "—"}</span>
            <p className="nl-geist text-[9px] text-[#c09aff] mt-1.5">{j.initiatives ? `Tracked Initiative${j.initiatives === 1 ? "" : "s"}` : "No tracked initiatives yet"}</p>
          </div>
          <div className="rounded-xl p-3.5 min-h-[96px]" style={METRIC_STYLE}>
            <div className="flex items-center gap-1.5">
              <p className="nl-geist font-bold text-[8.5px] tracking-[0.08em] text-[#8b93a9]">REGULATORY COVERAGE TONE</p>
              <InfoTip label="Coverage tone methodology">
                Deterministic tone score of NewsLayer's verified policy coverage for this
                jurisdiction over the last 30 days. Each story is lexicon-scored for regulatory
                posture (supportive vs restrictive); the net balance maps to −100…+100. Requires
                at least 3 scored stories. Not an official government metric and not market sentiment.
              </InfoTip>
            </div>
            {j.sentiment ? (
              <>
                <span className="block nl-sg font-medium text-[22px] leading-none mt-2"
                  style={{ color: SENT_COLOR(j.sentiment.score) }} data-testid="reg-sentiment-label">
                  {j.sentiment.label}
                </span>
                <SentimentSlider score={j.sentiment.score} />
                <p className="nl-geist text-[9px] text-[#6f7a94] mt-1.5">
                  {j.sentiment.coverage_stories} scored policy stories · 30D
                </p>
                {(j.sentiment_history || []).filter((p: any) => p.v != null).length >= 2 && (
                  <div className="mt-2" data-testid="reg-sentiment-history">
                    <AreaSpark points={j.sentiment_history.filter((p: any) => p.v != null).map((p: any) => p.v)}
                      color={SENT_COLOR(j.sentiment.score)} className="w-full h-6" />
                    <p className="nl-geist text-[8px] text-[#5b6478] mt-0.5">30D tone trend</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <span className="block nl-sg font-medium text-[22px] leading-none text-[#8b93a9] mt-2">—</span>
                <SentimentBar />
                <p className="nl-geist text-[9px] text-[#6f7a94] mt-1.5">Not enough scored coverage yet</p>
              </>
            )}
          </div>
        </div>
        <div className="mt-auto pt-4 text-center">
          <Link to={`/jurisdiction/${j.code}`} data-testid="reg-spotlight-profile-link"
            className="nl-geist font-semibold text-[11px] text-[#b88cff] hover:text-white motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8b5cff] rounded">
            View full jurisdiction profile →
          </Link>
        </div>
      </div>
    </div>
  );
};

export const RegulationIntelligenceSection = ({ initialData }: { initialData?: any }) => {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: ["regulation-intelligence"],
    queryFn: async () => (await api.get("/regulation/intelligence")).data,
    staleTime: 5 * 60 * 1000,
    initialData,
  });
  const [selected, setSelected] = useState<string | null>(null);
  const byName = useMemo(() => Object.fromEntries((data?.jurisdictions || []).map((j: any) => [j.name, j])), [data]);
  useEffect(() => {
    if (!selected && data?.spotlight_default) {
      setSelected(byName[data.spotlight_default]?.code || null);
    }
  }, [data, byName, selected]);

  if (!data) {
    return (
      <section data-testid="regulation-intelligence" className="mt-12 sm:mt-16">
        <div className="rounded-2xl border border-white/[0.08] bg-[#050508] min-h-[420px] animate-pulse motion-reduce:animate-none" />
      </section>
    );
  }

  const jurisdictions = data.jurisdictions || [];
  const spotJ = jurisdictions.find((j: any) => j.code === selected) || jurisdictions[0];

  return (
    <TooltipProvider delayDuration={150}>
      <section data-testid="regulation-intelligence" className="relative mt-12 sm:mt-16 overflow-x-clip">
        <div aria-hidden className="pointer-events-none absolute -top-12 left-[6%] w-[460px] h-[240px] rounded-full bg-[#8b5cff]/[0.06] blur-[90px] hidden xl:block" />
        <div aria-hidden className="pointer-events-none absolute -top-8 right-[8%] w-[380px] h-[210px] rounded-full bg-[#14cfe6]/[0.045] blur-[90px] hidden xl:block" />

        <div className="relative flex flex-wrap items-end justify-between gap-x-8 gap-y-5">
          <div className="min-w-0" style={{ fontFamily: "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
            <h2 className="text-[#f2f5fc] font-bold text-[32px] leading-[38px] sm:text-[44px] sm:leading-[52px] tracking-tight">
              Regulation Intelligence
            </h2>
            <p className="text-[#969cab] text-[15px] sm:text-[16px] leading-[22px] mt-1 max-w-[762px]">
              Real-time regulatory intelligence across jurisdictions worldwide.
            </p>
          </div>
          <div className="flex items-center gap-7 sm:gap-9 flex-wrap pb-1">
            <div>
              <p className="nl-geist font-bold text-[10.5px] tracking-[0.08em] text-[#22d38f] inline-flex items-center gap-1.5">
                <span className="w-[7px] h-[7px] rounded-full bg-[#22d38f] shadow-[0_0_8px_rgba(34,211,143,0.8)] motion-safe:animate-pulse" aria-hidden /> LIVE DATA
              </p>
              <p className="nl-geist text-[10px] text-[#7a86a0] mt-1" data-testid="reg-updated-at">
                {data.updated_at ? `Updated ${dayjs(data.updated_at).fromNow()}` : "Awaiting first update"}
              </p>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center shrink-0"
                style={{ background: "#0b0b10", boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.1)" }} aria-hidden>
                <Landmark className="w-4 h-4 text-[#8fa2c8]" />
              </span>
              <span>
                <p className="nl-geist font-bold text-[12px] text-[#eef1f9]" data-testid="reg-jurisdictions-count">
                  {data.jurisdictions_monitored} Jurisdictions
                </p>
                <p className="nl-geist text-[9.5px] text-[#7a86a0] mt-0.5">Monitored</p>
              </span>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center shrink-0"
                style={{ background: "#100e16", boxShadow: "inset 0 0 0 1px rgba(245,180,32,0.35)" }} aria-hidden>
                <Crown className="w-4 h-4 text-[#f5b420]" />
              </span>
              <span>
                <p className="nl-geist font-bold text-[12px] text-[#f5b420]">Premium Intelligence</p>
                <Link to="/premium" data-testid="reg-header-premium-link"
                  className="block nl-geist font-semibold text-[9.5px] text-[#b88cff] hover:text-white mt-0.5 motion-safe:transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8b5cff] rounded">
                  {user?.is_premium ? "Full access active" : "Unlock full access →"}
                </Link>
              </span>
            </div>
          </div>
        </div>

        <div className="relative mt-6 grid grid-cols-1 xl:grid-cols-[minmax(0,65fr)_minmax(0,35fr)] gap-5">
          <RegHeatMap jurisdictions={jurisdictions} selected={selected} onSelect={setSelected} />
          {spotJ && <Spotlight j={spotJ} />}
        </div>

        <AlertsRail alerts={data.alerts || []} />
        <PipelinePanel pipeline={data.pipeline} />

        <div className="mt-5 grid grid-cols-1 xl:grid-cols-3 gap-5">
          <TrendsPanel trends={data.trends} />
          <MoversPanel movers={data.movers || []} />
          <CalendarPanel calendar={data.calendar || []} />
        </div>

        <PremiumRail />

        <p className="nl-geist text-[9px] text-[#5b6478] mt-4 text-center">
          Metrics are derived from NewsLayer policy coverage and tracked regulation records — hover any <span className="font-serif italic font-semibold">i</span> marker for methodology. Derived figures are not official government statistics.
        </p>
      </section>
    </TooltipProvider>
  );
};
